import { KIND_LABELS } from './skillEditView';
import type { MimirChannel, MimirPreview, MimirSendInput } from './types';

// View rules for the Send to Slack section on Mimir's card (OMT-1839).

/** Which intro a channel can take: the one matching a watched channel's mode, else both. */
export const introChoices = (channel: MimirChannel): ('all' | 'mention')[] =>
  channel.mode === 'all' || channel.mode === 'mention'
    ? [channel.mode]
    : ['all', 'mention'];

/** A draft intro goes to the test channel only. */
export const draftAllowed = (channel: MimirChannel): boolean =>
  channel.mode === 'test';

const MENTION_NAME = '@Mimir (Engineering Agent)';

/** What Mimir would post, line by line: its own mention shown by name, then the Powered-by line. */
export const previewLines = (
  preview: MimirPreview,
  botUserId: string,
): string[] => {
  const lines = preview.text
    .split(`<@${botUserId}>`)
    .join(MENTION_NAME)
    .split('\n');
  return preview.poweredBy ? [...lines, preview.poweredBy] : lines;
};

const sameInput = (a: MimirSendInput, b: MimirSendInput): boolean =>
  a.channel === b.channel &&
  a.message === b.message &&
  (a.intro ?? null) === (b.intro ?? null) &&
  !!a.draft === !!b.draft &&
  (a.text ?? '') === (b.text ?? '') &&
  (a.threadLink ?? '') === (b.threadLink ?? '');

/** The preview no longer matches the form: it isn't what Mimir would post now. */
export const previewStale = (state: {
  preview: MimirPreview | null;
  previewed: MimirSendInput | null;
  current: MimirSendInput;
}): boolean =>
  !!state.preview &&
  (!state.previewed || !sameInput(state.previewed, state.current));

/** Send needs a clean preview of exactly what is now on the form, and no send in flight. */
export const sendDisabled = (state: {
  preview: MimirPreview | null;
  previewed: MimirSendInput | null;
  current: MimirSendInput;
  sending: boolean;
}): boolean =>
  state.sending ||
  !state.preview ||
  !state.previewed ||
  state.preview.found.length > 0 ||
  !sameInput(state.previewed, state.current);

/** Client-data kinds in words; a kind this page doesn't know is "client data". */
export const kindsText = (found: string[]): string =>
  found
    .map((k) =>
      Object.prototype.hasOwnProperty.call(KIND_LABELS, k)
        ? KIND_LABELS[k]
        : 'client data',
    )
    .join(', ');

const FALLBACK = 'Something went wrong. Check Activity before sending again.';

const MESSAGES: Record<string, string> = {
  posted: 'Posted.',
  already: 'Already posted a few minutes ago; not sent again.',
  not_member:
    "Mimir isn't in that channel any more. Add it in Slack, then refresh the list.",
  changed:
    'The text changed since the preview (maybe a new intro version). Preview again.',
  draft_not_test: 'A draft can only go to the test channel.',
  wrong_intro: 'That channel takes the other intro.',
  no_draft: 'You have no draft of this intro.',
  card_channel: "Mimir doesn't post in its card channel.",
  not_approver: "Only Mimir's approvers can send.",
  slack_error:
    "Slack refused the post. Try again; if it repeats, check the listener's logs.",
  mimir_unavailable: "Mimir's listener can't be reached right now.",
  skills_unavailable:
    "Mimir's skills can't be read right now. Try again in a minute.",
  missing_token:
    "You don't have access, or your session has expired. Sign in again and retry.",
  invalid_token:
    "You don't have access, or your session has expired. Sign in again and retry.",
  auth_unavailable:
    "The agent service can't check your sign-in right now. Try again shortly.",
  bad_link: "That isn't a Slack message link.",
  link_channel: 'The link is from a different channel.',
  too_long:
    'That text is too long for one Slack message. Shorten it and preview again.',
  invalid_request: "The request wasn't valid. Refresh the page and try again.",
};

/** The copy for a send's outcome or refusal code; any code not listed gets the fallback. */
export const outcomeMessage = (code: string, found: string[]): string =>
  code === 'client_data'
    ? `Not sent: the text holds ${kindsText(found)}. Remove it and preview again.`
    : (MESSAGES[code] ?? FALLBACK);
